import type {
  DayKey,
  Dog,
  EventClassRequirement,
  EventRequirement,
  EventResultRequirementsByDate,
  Registration,
} from './types'
import { calendarDayOf, TIME_ZONE } from './lib/day'
import { keysOf } from './lib/typeGuards'
import { isModernFinnishRegNo } from './lib/validation'
import { NOME_A_CH_requirements, NOME_B_CH_requirements, NOWT_CH_requirements } from './rules_ch'

export const RULE_DATES = [
  '1977-01-01',
  '1986-01-01',
  '1991-01-01',
  '1999-01-01',
  '2006-04-01',
  '2009-01-01',
  '2016-04-01',
  '2023-04-15',
] as const

export type RuleDate = (typeof RULE_DATES)[number]

/**
 * The rules in force on a trial's day: the latest rule date on or before it. A day is a key; a stored
 * row's instant is read as its Finnish day, and a value with no day gets the latest rules.
 */
export function getRuleDate(date: string, available: Readonly<RuleDate[]> = RULE_DATES): RuleDate {
  const day = calendarDayOf(date, TIME_ZONE)
  for (let i = 1; day && i < available.length; i++) {
    if (available[i] > day) {
      return available[i - 1]
    }
  }
  const latestRuleDate = available.at(-1)
  if (!latestRuleDate) throw new Error('At least one rule date is required')
  return latestRuleDate
}

const validateDogForSM = (dog: Partial<Dog>) =>
  dog.regNo && dog.kcId && isModernFinnishRegNo(dog.regNo) ? false : 'dogSM'

export function getRequirements(eventType: string, regClass: Registration['class'], date: DayKey) {
  const eventRequirements = REQUIREMENTS[eventType] || {}
  const classRequirements = regClass && (eventRequirements as EventClassRequirement)[regClass]
  const requirements = classRequirements ?? (eventRequirements as EventRequirement)
  let results: EventResultRequirementsByDate | undefined
  if (requirements.results) {
    const resultRequirements = requirements.results
    const ruleDates = keysOf(resultRequirements)
    const ruleDate = getRuleDate(date, ruleDates)
    results = {
      date: ruleDate,
      rules: resultRequirements[ruleDate] ?? [],
    }
  }
  return results
}

export const REQUIREMENTS: { [key: string]: EventRequirement | EventClassRequirement } = {
  NKM: {
    results: {
      '2016-04-01': [[{ count: 2, result: 'VOI1', type: 'NOME-B' }], [{ cert: true, count: 2, type: 'NOWT' }]],
      '2023-04-15': [[{ count: 2, result: 'VOI1', type: 'NOME-B' }], [{ cert: true, count: 2, type: 'NOWT' }]],
    },
  },
  'NOME-A': {
    results: {
      '2009-01-01': [[{ count: 1, result: 'AVO1', type: 'NOME-B' }], [{ count: 1, result: 'AVO1', type: 'NOWT' }]],
      '2016-04-01': [[{ count: 2, result: 'AVO1', type: 'NOME-B' }], [{ count: 2, result: 'AVO1', type: 'NOWT' }]],
      '2023-04-15': [
        [{ count: 1, result: 'AVO1', type: 'NOME-B' }],
        [{ count: 1, result: 'AVO1', type: 'NOWT' }],
        [{ count: 1, result: 'EXC', type: 'NOME-A KV' }],
        [{ count: 1, result: 'VG', type: 'NOME-A KV' }],
        [{ count: 1, result: 'G', type: 'NOME-A KV' }],
      ],
    },
  },
  'NOME-A SM': {
    dog: validateDogForSM,
    results: {
      '2023-04-15': NOME_A_CH_requirements,
    },
  },
  'NOME-B': {
    ALO: {
      results: {
        '1991-01-01': [{ count: 1, result: 'NOU1', type: 'NOU' }],
      },
    },
    AVO: {
      results: {
        '1991-01-01': [{ count: 1, result: 'ALO1', type: 'NOME-B' }],
        '1999-01-01': [{ count: 2, result: 'ALO1', type: 'NOME-B' }],
        '2006-04-01': [{ count: 1, result: 'ALO1', type: 'NOME-B' }],
        '2009-01-01': [{ count: 1, result: 'ALO1', type: 'NOME-B' }],
        '2016-04-01': [{ count: 2, result: 'ALO1', type: 'NOME-B' }],
        '2023-04-15': [{ count: 1, result: 'ALO1', type: 'NOME-B' }],
      },
    },
    VOI: {
      results: {
        '1977-01-01': [{ count: 1, result: 'AVO1', type: 'NOME-B' }],
        '1986-01-01': [{ count: 2, result: 'AVO1', type: 'NOME-B' }],
        '2006-04-01': [{ count: 1, result: 'AVO1', type: 'NOME-B' }],
        '2009-01-01': [{ count: 2, result: 'AVO1', type: 'NOME-B' }],
        '2016-04-01': [{ count: 2, result: 'AVO1', type: 'NOME-B' }],
        '2023-04-15': [{ count: 1, result: 'AVO1', type: 'NOME-B' }],
      },
    },
  },
  'NOME-B SM': {
    dog: validateDogForSM,
    results: {
      '2023-04-15': NOME_B_CH_requirements,
    },
  },
  NOU: {
    age: 9,
    breedCode: ['122', '111', '121', '312', '110', '263'],
  },
  NOWT: {
    ALO: {
      results: {
        '2006-04-01': [{ count: 1, result: 'NOU1', type: 'NOU' }],
        '2009-01-01': [{ count: 1, result: 'NOU1', type: 'NOU' }],
        '2016-04-01': [{ count: 1, result: 'NOU1', type: 'NOU' }],
        '2023-04-15': [{ count: 1, result: 'NOU1', type: 'NOU' }],
      },
    },
    AVO: {
      results: {
        '2006-04-01': [{ count: 1, result: 'ALO1', type: 'NOWT' }],
        '2009-01-01': [{ count: 1, result: 'ALO1', type: 'NOWT' }],
        '2016-04-01': [{ count: 1, result: 'ALO1', type: 'NOWT' }],
        '2023-04-15': [{ count: 1, result: 'ALO1', type: 'NOWT' }],
      },
    },
    VOI: {
      results: {
        '2006-04-01': [{ count: 1, result: 'AVO1', type: 'NOWT' }],
        '2009-01-01': [{ count: 1, result: 'AVO1', type: 'NOWT' }],
        '2016-04-01': [{ count: 1, result: 'AVO1', type: 'NOWT' }],
        '2023-04-15': [{ count: 1, result: 'AVO1', type: 'NOWT' }],
      },
    },
  },
  'NOWT SM': {
    dog: validateDogForSM,
    results: {
      '2023-04-15': NOWT_CH_requirements,
    },
  },
}
